import React, { FC } from 'react';
import styled from 'styled-components';
import { renderFont } from '@empiriecom/module-components/ThemeProvider';
import { Product } from '@/interfaces';
import useConfig from '@empiriecom/module-components/hooks/useConfig';
import { DynamicYield } from '@empiriecom/module-components/DynamicYield';
import { FragmentConfiguration } from '@/config';
import OOCVTable from './OOCVTable';

interface ProductDescriptionProps {
  product: Product;
}

const Description = styled.div`
  .longDescription {
    margin: 1rem 0;
  }
  .longDescription ul {
    padding-left: 1rem;
  }
  table.tmpArticleDetailTable {
    margin: 0;
    width: 100%;
    border-color: #f4f4f4;
    border-width: 1px;
    border-style: solid;
    ~ table {
      border-width: 0 1px 1px;
    }
    caption {
      background-color: #f4f4f4;
      text-align: left;
      padding: 0.625rem 0.5rem;
      margin-top: 0.75rem;
    }
    td {
      border-color: #f4f4f4;
      border-width: 1px 1px 0;
      border-style: solid;
      padding: 0.5rem;
      width: 60%;
      word-break: break-word;
      &&.left {
        width: 40%;
        ${({ theme }) => renderFont('base', 'bold', theme)}
        vertical-align: top;
      }
    }
    ~ table tr:first-child td {
      border-top-width: 0;
    }
  }
`;

const SellingPointsList = styled.ul`
  padding-left: 1rem;
`;

/**
 * This is the product description. If the product has `sellingPoints` available those will
 * be rendered as a list. The product's other description strings will always be rendered.
 */
export const ProductDescription: FC<ProductDescriptionProps> = ({
  product: { attributes = {} },
}) => {
  const { description } = attributes;
  const { dynamicYieldEnabled } = useConfig<FragmentConfiguration>();
  const hasOOCVJson = description.values.some((value) => value.type === 'oocvJSON');
  return (
    <>
      {description && (
        <Description data-testid="description">
          {description.values &&
            description.values.map(({ type, values }) => {
              if (type === 'sellingPoints') {
                return (
                  <SellingPointsList data-testid="sellingpoints" key={type}>
                    {values.map((value, index) => (
                      // use index to make sure that the keys are always unique
                      <li key={value.toString().concat(index.toString())}>{value.toString()}</li>
                    ))}
                  </SellingPointsList>
                );
              }
              return (
                <div key={type}>
                  {type !== 'oocv' && type !== 'oocvJSON' && (
                    <>
                      {dynamicYieldEnabled && (
                        <>
                          <DynamicYield dataDyId="dv_badge" />
                          <DynamicYield dataDyId="dv-horizontal-banner" />
                        </>
                      )}
                      <div
                        /* Description strings can contain html; so we need to set them as innerHtml here */
                        /* eslint-disable-next-line react/no-danger */
                        dangerouslySetInnerHTML={{
                          __html: values.join(''),
                        }}
                        className={type}
                        data-testid={type}
                      />
                    </>
                  )}
                  {!hasOOCVJson && type === 'oocv' && (
                    <div
                      /* Description strings can contain html; so we need to set them as innerHtml here */
                      /* eslint-disable-next-line react/no-danger */
                      dangerouslySetInnerHTML={{
                        __html: values.join(''),
                      }}
                      className={type}
                      data-testid={type}
                    />
                  )}
                  {type === 'oocvJSON' && <OOCVTable values={values} />}
                </div>
              );
            })}
        </Description>
      )}
    </>
  );
};

export default ProductDescription;
